/**
 * PricingModal Component (F16)
 * Token purchase modal with tier selection and payment provider integration
 *
 * Tiers:
 * - Starter: $10 → 500 tokens
 * - Standard: $25 → 1,500 tokens (Popular)
 * - Pro: $50 → 5,000 tokens
 *
 * Payment Providers:
 * - Payoneer
 * - Coinbase Commerce
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckIcon from '@mui/icons-material/Check';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import { PRICING_TIERS, getQAIPricing, createCheckout } from '../../api/qaiApi';

/**
 * PricingModal Component
 * @param {boolean} open - Modal visibility
 * @param {function} onClose - Close handler
 * @param {function} onSelectTier - Legacy tier selection handler (optional)
 * @param {number} currentBalance - User's current token balance
 * @param {string} token - JWT token for API calls
 */
const PricingModal = ({
  open = false,
  onClose,
  onSelectTier,
  currentBalance = 0,
  token
}) => {
  const [selectedTier, setSelectedTier] = useState('standard');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pricingData, setPricingData] = useState({
    tiers: PRICING_TIERS,
    availableProviders: []
  });

  // Fetch pricing data when modal opens
  useEffect(() => {
    if (open) {
      fetchPricing();
    }
  }, [open]);

  const fetchPricing = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getQAIPricing();
      setPricingData(data);
      // Auto-select first available provider
      if (data.availableProviders?.length > 0) {
        setSelectedProvider(data.availableProviders[0]);
      }
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
      // Use fallback tiers but show no providers
      setPricingData({ tiers: PRICING_TIERS, availableProviders: [] });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTier = (tierId) => {
    setSelectedTier(tierId);
    setError(null);
  };

  const handleProviderChange = (event, newProvider) => {
    if (newProvider !== null) {
      setSelectedProvider(newProvider);
      setError(null);
    }
  };

  const handlePurchase = async () => {
    if (!selectedTier || isProcessing) return;

    // Check if providers are available
    if (pricingData.availableProviders.length === 0) {
      setError('Payment is not currently available. Please try again later.');
      return;
    }

    if (!selectedProvider) {
      setError('Please select a payment method.');
      return;
    }

    // Get token from localStorage if not passed as prop
    const authToken = token || localStorage.getItem('qai_token') || localStorage.getItem('token');
    if (!authToken) {
      setError('Please log in to purchase tokens.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await createCheckout(authToken, selectedTier, selectedProvider);

      if (result.checkoutUrl) {
        // Redirect to payment provider
        window.location.href = result.checkoutUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout failed:', err);
      setError(err.message || 'Failed to create checkout. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const hasProviders = pricingData.availableProviders.length > 0;
  const tiers = pricingData.tiers || PRICING_TIERS;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#0F0F14',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          maxWidth: '720px'
        }
      }}
    >
      {/* ════════════════════════════════════════════════════════════════════════
          HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px 24px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <BoltIcon sx={{ color: '#FFFFFF', fontSize: '1.5rem' }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: '#FFFFFF',
                fontSize: '1.25rem',
                fontWeight: 600,
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
            >
              Get More Tokens
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.875rem'
              }}
            >
              Current balance: {currentBalance} tokens
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              color: '#FFFFFF',
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ════════════════════════════════════════════════════════════════════════
          CONTENT
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogContent sx={{ padding: '24px' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#7c3aed' }} />
          </Box>
        ) : (
          <>
            {/* Error Alert */}
            {error && (
              <Alert
                severity="error"
                onClose={() => setError(null)}
                sx={{
                  mb: 2,
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#EF4444',
                  '& .MuiAlert-icon': { color: '#EF4444' }
                }}
              >
                {error}
              </Alert>
            )}

            {/* Pricing Tiers */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2
              }}
            >
              {tiers.map((tier) => (
                <PricingCard
                  key={tier.id}
                  tier={tier}
                  isSelected={selectedTier === tier.id}
                  onSelect={() => handleSelectTier(tier.id)}
                />
              ))}
            </Box>

            {/* Payment Provider Selection */}
            {hasProviders ? (
              <Box sx={{ mt: 3 }}>
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    mb: 1.5
                  }}
                >
                  Payment Method
                </Typography>
                <ToggleButtonGroup
                  value={selectedProvider}
                  exclusive
                  onChange={handleProviderChange}
                  sx={{ width: '100%' }}
                >
                  {pricingData.availableProviders.includes('payoneer') && (
                    <ToggleButton
                      value="payoneer"
                      sx={{
                        flex: 1,
                        color: 'rgba(255, 255, 255, 0.7)',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                        textTransform: 'none',
                        py: 1.5,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(124, 58, 237, 0.15)',
                          color: '#FFFFFF',
                          borderColor: '#7c3aed',
                          '&:hover': {
                            backgroundColor: 'rgba(124, 58, 237, 0.2)'
                          }
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        }
                      }}
                    >
                      <CreditCardIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                      Payoneer
                    </ToggleButton>
                  )}
                  {pricingData.availableProviders.includes('coinbase') && (
                    <ToggleButton
                      value="coinbase"
                      sx={{
                        flex: 1,
                        color: 'rgba(255, 255, 255, 0.7)',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                        textTransform: 'none',
                        py: 1.5,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(124, 58, 237, 0.15)',
                          color: '#FFFFFF',
                          borderColor: '#7c3aed',
                          '&:hover': {
                            backgroundColor: 'rgba(124, 58, 237, 0.2)'
                          }
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        }
                      }}
                    >
                      <CurrencyBitcoinIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                      Crypto (Coinbase)
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 3,
                  padding: '16px',
                  backgroundColor: 'rgba(251, 191, 36, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(251, 191, 36, 0.2)'
                }}
              >
                <Typography
                  sx={{
                    color: '#FBBF24',
                    fontSize: '0.875rem',
                    textAlign: 'center'
                  }}
                >
                  Payment is coming soon. Token purchases will be available shortly.
                </Typography>
              </Box>
            )}

            {/* Token Usage Info */}
            <Box
              sx={{
                marginTop: 3,
                padding: '16px',
                backgroundColor: 'rgba(124, 58, 237, 0.08)',
                borderRadius: '12px',
                border: '1px solid rgba(124, 58, 237, 0.2)'
              }}
            >
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: 1
                }}
              >
                Token Usage Guide
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <TokenUsageRow label="Chat message" cost={5} />
                <TokenUsageRow label="EA Analysis" cost={10} />
                <TokenUsageRow label="Trading Signals" cost={15} />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      {/* ════════════════════════════════════════════════════════════════════════
          FOOTER
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogActions
        sx={{
          padding: '16px 24px 24px',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handlePurchase}
          disabled={isProcessing || isLoading || !hasProviders}
          sx={{
            background: hasProviders
              ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)'
              : 'rgba(124, 58, 237, 0.3)',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 24px',
            borderRadius: '8px',
            '&:hover': {
              background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)'
            },
            '&.Mui-disabled': {
              background: 'rgba(124, 58, 237, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)'
            }
          }}
        >
          {isProcessing ? (
            <>
              <CircularProgress size={16} sx={{ color: '#FFFFFF', mr: 1 }} />
              Processing...
            </>
          ) : hasProviders ? (
            `Purchase ${tiers.find(t => t.id === selectedTier)?.name || ''}`
          ) : (
            'Coming Soon'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Pricing Card Component
 */
const PricingCard = ({ tier, isSelected, onSelect }) => {
  return (
    <Box
      onClick={onSelect}
      sx={{
        position: 'relative',
        padding: '24px',
        borderRadius: '12px',
        border: isSelected
          ? '2px solid #7c3aed'
          : '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: isSelected
          ? 'rgba(124, 58, 237, 0.1)'
          : 'rgba(255, 255, 255, 0.02)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: isSelected ? '#7c3aed' : 'rgba(124, 58, 237, 0.5)',
          backgroundColor: isSelected
            ? 'rgba(124, 58, 237, 0.15)'
            : 'rgba(124, 58, 237, 0.05)'
        }
      }}
    >
      {/* Popular Badge */}
      {tier.popular && (
        <Chip
          label="Popular"
          size="small"
          sx={{
            position: 'absolute',
            top: -10,
            right: 12,
            backgroundColor: '#7c3aed',
            color: '#FFFFFF',
            fontSize: '0.75rem',
            fontWeight: 600,
            height: '20px'
          }}
        />
      )}

      {/* Selection Indicator */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: '#7c3aed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CheckIcon sx={{ color: '#FFFFFF', fontSize: '0.875rem' }} />
        </Box>
      )}

      {/* Tier Name */}
      <Typography
        sx={{
          color: '#FFFFFF',
          fontSize: '1rem',
          fontWeight: 600,
          marginBottom: 1,
          textAlign: 'center'
        }}
      >
        {tier.name}
      </Typography>

      {/* Price */}
      <Box sx={{ textAlign: 'center', marginBottom: 2 }}>
        <Typography
          component="span"
          sx={{
            color: '#FFFFFF',
            fontSize: '2rem',
            fontWeight: 700,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          }}
        >
          ${tier.price}
        </Typography>
      </Box>

      {/* Tokens */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          padding: '8px 12px',
          backgroundColor: 'rgba(124, 58, 237, 0.15)',
          borderRadius: '8px'
        }}
      >
        <BoltIcon sx={{ color: '#a855f7', fontSize: '1rem' }} />
        <Typography
          sx={{
            color: '#a855f7',
            fontSize: '0.875rem',
            fontWeight: 600
          }}
        >
          {tier.tokens.toLocaleString()} tokens
        </Typography>
      </Box>

      {/* Value indicator */}
      <Typography
        sx={{
          color: 'rgba(255, 255, 255, 0.4)',
          fontSize: '0.75rem',
          textAlign: 'center',
          marginTop: 1
        }}
      >
        ${(tier.price / tier.tokens * 100).toFixed(1)}¢ per token
      </Typography>
    </Box>
  );
};

/**
 * Token Usage Row Component
 */
const TokenUsageRow = ({ label, cost }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8125rem' }}>
      {label}
    </Typography>
    <Typography sx={{ color: '#a855f7', fontSize: '0.8125rem', fontWeight: 600 }}>
      {cost} tokens
    </Typography>
  </Box>
);

export default PricingModal;
